import React from 'react';

import { Avatar, Paper } from '@material-ui/core';
import { getColorFromColorEnumForLevelUp } from 'screens/Levelup/constants';
import styled from 'styled-components';
import { Divider1, FlexContainer, StyledTypography, VerticallyAlignedFlexContainer } from 'styles/common';

import { convertDateToString4 } from 'constants/helper';
import theme from 'constants/theme';

import { Typography12, Typography13, Typography6 } from 'uiToolkit/Typography';

const ProgressBox = ({ label, progress, percentage, mappedLevelUpProgressStatuses = [], style }) => {
  const levelUpProgress = mappedLevelUpProgressStatuses?.find(({ value }) => value === progress) ?? {};
  const textColor = levelUpProgress.color;

  const progressLabel = levelUpProgress.label;

  return (
    <FlexContainer style={{ ...style, flexDirection: 'column' }}>
      <Typography12 color={theme.palette.grey.dark}>{label}</Typography12>
      <Typography6 color={textColor}>
        {progressLabel} ({percentage}%)
      </Typography6>
    </FlexContainer>
  );
};

const DateField = ({ timestamp }) => {
  const date = new Date(timestamp);
  if (!timestamp) {
    return null;
  }
  return (
    <Container>
      <StyledTypography color="inherit" display="inline" variant="body2" leftGutter={0.25}>
        {convertDateToString4(date)}
      </StyledTypography>{' '}
    </Container>
  );
};

const UserCheckIn = ({ employeeProfile, comment }) => {
  const { profilePhoto, displayName } = employeeProfile ?? {};
  return (
    <FlexContainer style={{ flexDirection: 'column' }}>
      <VerticallyAlignedFlexContainer>
        <Avatar style={{ height: '24px', width: '24px', marginRight: '8px' }} src={profilePhoto} />
        <Typography13 weight={600}>{displayName}</Typography13>
      </VerticallyAlignedFlexContainer>
      <TextContainer className="text-area">
        <Typography13>{comment}</Typography13>
      </TextContainer>
    </FlexContainer>
  );
};

const ActivityGraphTooltip = ({ payload, levelUpProgressStatuses = [] }) => {
  const mappedLevelUpProgressStatuses = levelUpProgressStatuses.map((status) => ({
    value: status.progress,
    label: status.displayName,
    color: getColorFromColorEnumForLevelUp(status.colour),
    description: status.description,
  }));

  const {
    comment,
    createdTS,
    currentProgress,
    employeeProfile,
    previousProgress,
    previousProgressPercentage,
    progressPercentage,
  } = payload[0]?.payload ?? {};

  return (
    <StyledPaper>
      <ProgressBox
        label="From"
        progress={previousProgress}
        percentage={previousProgressPercentage}
        mappedLevelUpProgressStatuses={mappedLevelUpProgressStatuses}
      />
      <VerticallyAlignedFlexContainer>
        <ProgressBox
          label="To"
          progress={currentProgress}
          percentage={progressPercentage}
          mappedLevelUpProgressStatuses={mappedLevelUpProgressStatuses}
          style={{ marginTop: '16px' }}
        />
        <DateField timestamp={createdTS} />
      </VerticallyAlignedFlexContainer>
      <Divider1 style={{ margin: '16px 0' }} />
      <UserCheckIn employeeProfile={employeeProfile} comment={comment} />
    </StyledPaper>
  );
};

const StyledPaper = styled(Paper)`
  box-shadow: 2px 4px 4px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 16px;
`;

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props?.size === 'big' ? '0.5rem 1rem' : '0.25rem 0.5rem')};
  &:hover {
    cursor: ${(props) => (props?.clickable === false ? 'default' : 'pointer')};
  }
  height: 32px;
  min-width: 64px;
  border-radius: 3px;
  background-color: ${theme.palette.grey.main}33;
  margin-left: 42px;
`;

const TextContainer = styled('div')`
  overflow: auto;
  max-height: 70px;
`;

export default ActivityGraphTooltip;
